import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { formatINR } from '../../lib/quote';
import { IMG_FALLBACK } from '../../lib/images';
import { unitName, kindText, MAX_COMPARE } from '../../data/compare';

// "+ Add AC" bottom sheet: a search box, filter chips built from the data,
// and the matching ACs, in stock first.

const hay = (u) => [
  u.brand, u.model.title, u.idu, u.model.series, `${Number(u.model.soldAs).toFixed(1)} ton`,
  `${u.model.stars} star`, u.model.inverter === true ? 'inverter' : u.model.inverter === false ? 'fixed speed non-inverter' : '', u.type,
  u.clearance ? 'clearance old stock' : '', u.heats ? 'hot and cold heating' : '',
].join(' ').toLowerCase();

// Chip groups. A group with one choice cannot change the list, so it is
// not shown; a new brand or a window AC makes its group appear by itself.
function groupsFrom(units) {
  const g = (key, label, pick, text, sort) => {
    const vals = [...new Set(units.map(pick))].filter((v) => v != null).sort(sort);
    return vals.length > 1 ? { key, label, options: vals.map((v) => ({ v, label: text(v) })) } : null;
  };
  return [
    g('brand', 'Brand', (u) => u.brand, (v) => v, (a, b) => String(a).localeCompare(String(b))),
    g('size', 'Size', (u) => u.model.soldAs, (v) => `${Number(v).toFixed(1)} Ton`, (a, b) => Number(a) - Number(b)),
    g('type', 'Type', (u) => u.type, (v) => (v === 'window' ? 'Window' : 'Split'), (a, b) => String(a).localeCompare(String(b))),
    g('stars', 'Stars', (u) => u.model.stars, (v) => `${v} Star`, (a, b) => b - a),
    g('speed', 'Speed', (u) => u.model.inverter, (v) => (v ? 'Inverter' : 'Fixed speed'), (a, b) => Number(b) - Number(a)),
  ].filter(Boolean);
}

const valueOf = {
  brand: (u) => u.brand, size: (u) => u.model.soldAs, type: (u) => u.type,
  stars: (u) => u.model.stars, speed: (u) => u.model.inverter,
};

export default function AddSheet({ open, onClose, units, selected, onPick, replacing }) {
  const [q, setQ] = useState('');
  const [f, setF] = useState({});
  const [stockOnly, setStockOnly] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [open, onClose]);

  const groups = useMemo(() => groupsFrom(units), [units]);
  const list = useMemo(() => {
    // "1.5 ton 5 star" -> ["1.5 ton", "5 star"], so "5" cannot match "1.5".
    const words = q.toLowerCase()
      .replace(/(\d+(?:\.\d+)?)\s*(ton|star)s?\b/g, (_, n, u) => `${u === 'ton' ? Number(n).toFixed(1) : n}_${u}`)
      .split(/\s+/).filter(Boolean).map((w) => w.replace('_', ' '));
    return units
      .filter((u) => words.every((w) => hay(u).includes(w)))
      .filter((u) => Object.entries(f).every(([k, v]) => v == null || String(valueOf[k](u)) === String(v)))
      .filter((u) => !stockOnly || u.inStock)
      .sort((a, b) => Number(b.inStock) - Number(a.inStock) || a.price - b.price);
  }, [units, q, f, stockOnly]);

  if (!open) return null;
  // Changing one AC never counts as adding, so the limit does not apply.
  const full = !replacing && selected.length >= MAX_COMPARE;
  const chip = (on) => `shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 text-gray-700 hover:border-blue-400'}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose} data-testid="add-ac-sheet">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add an AC to compare"
        className="flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white sm:max-w-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <p className="text-sm font-bold text-gray-900">
            {replacing ? <>Change {unitName(replacing)} <span className="font-normal text-gray-500">· pick the AC to put in its place</span></>
              : <>Add an AC <span className="font-normal text-gray-500">· {selected.length} of {MAX_COMPARE} picked</span></>}
            {full && <span className="block font-normal text-red-600">{MAX_COMPARE} is the limit. Use Change on an AC, or remove one.</span>}
          </p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="add-ac-close" className="rounded-full p-1 text-gray-600 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="px-4 pt-3">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Type a brand, model code, size or stars. Like: 1.5 ton 5 star"
            data-testid="add-ac-search"
            className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-blue-500" />
          <div className="strip-scroll -mx-4 mt-2 flex gap-1.5 overflow-x-auto px-4 pb-1">
            <button type="button" onClick={() => setStockOnly((s) => !s)} className={chip(stockOnly)} data-testid="chip-in-stock">In stock</button>
            {groups.map((g) => g.options.map((o) => (
              <button key={`${g.key}-${o.v}`} type="button" data-testid={`chip-${g.key}-${String(o.v).replace(/\W+/g, '-')}`}
                onClick={() => setF((s) => ({ ...s, [g.key]: String(s[g.key]) === String(o.v) ? null : o.v }))}
                className={chip(String(f[g.key]) === String(o.v))}>
                {o.label}
              </button>
            )))}
          </div>
        </div>
        <ul className="flex-1 divide-y divide-gray-100 overflow-y-auto px-2 pb-4 pt-1" data-testid="add-ac-results">
          {list.length === 0 && <li className="px-2 py-6 text-center text-sm text-gray-500">Nothing matches. Try fewer words.</li>}
          {list.map((u) => {
            const on = selected.includes(u.id);
            return (
              <li key={u.id}>
                <button type="button" disabled={(!on && full) || (replacing && on)} onClick={() => onPick(u.id)}
                  data-testid={`add-ac-${u.id}`}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-blue-50 disabled:opacity-40 ${on ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}>
                  <img src={u.model.image} alt="" width="44" height="44" loading="lazy"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
                    className={`h-11 w-11 shrink-0 object-contain ${u.soldOut ? 'opacity-40' : ''}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-900">{unitName(u)} <span className="font-normal text-gray-500">· {Number(u.model.soldAs).toFixed(1)} Ton · {kindText(u)}{u.clearance ? ' · Clearance' : ''}</span></span>
                    <span className="block truncate text-xs text-gray-500">{u.model.title}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-sm font-bold text-gray-900">{formatINR(u.price)}</span>
                    <span className={`block text-[10px] font-semibold ${u.inStock ? 'text-blue-700' : u.soldOut ? 'text-red-600' : 'text-gray-500'}`}>
                      {u.inStock ? 'In stock' : u.soldOut ? 'Sold out' : 'On order'}
                    </span>
                    <span className={`block text-[11px] font-semibold ${on ? 'text-gray-500' : 'text-blue-700'}`}>
                      {replacing ? (on ? 'Already in' : 'Put this in') : on ? '✓ Added · remove' : 'Add'}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {!replacing && (
          <div className="border-t border-gray-200 px-4 py-3">
            <button type="button" onClick={onClose} data-testid="add-ac-done"
              className={`h-11 w-full rounded-lg text-sm font-semibold ${selected.length >= 2 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-500'}`}>
              {selected.length >= 2 ? `Compare ${selected.length} ACs` : selected.length === 1 ? 'Pick one more AC' : 'Pick two or more ACs'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
